import { NetZeroChart, NetZeroFooter, NetZeroHeader } from '../../ui';
import { useEffect, useState } from 'react';

import { CHART_STATES } from '../../utils/netZeroChartHelper';
import PropTypes from 'prop-types';
import { getBreakdownSteps } from '../../utils/netZeroHelper';
import netZeroContent from '../../utils/netZeroContent';
import styled from 'styled-components';
import { useNavigate } from 'react-router';

const CustomCard = styled.div`
  background-color: #fff;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  max-width: 720px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3);
  margin-top: 30px;

  @media screen and (max-width: 780px) {
    margin-top: 50px;
  }
`;

const BreakdownWrapper = styled.div`
  margin: 0;
  width: 100%;
  position: relative;
`;

const CustomCardFooter = styled.div`
  background-image: linear-gradient(78deg, #0ba360, #3cba92 100%);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  padding: 30px 50px;
  font-weight: 500;
  color: #fff;
  font-size: 22px;

  @media screen and (max-width: 780px) {
    font-size: 14px;
    font-weight: 600;
    padding: 20px 25px;
  }
`;

const stateArray = [
  CHART_STATES.NOW,
  CHART_STATES.SOLAR,
  CHART_STATES.INSULATE,
  CHART_STATES.EV,
  CHART_STATES.HEAT,
  CHART_STATES.GRID,
];

const getStepName = stepNumber => stateArray[stepNumber - 1];

const BreakdownContainer = ({ heatingMethod, usage, cars }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [breakdownSteps, setBreakdownSteps] = useState(undefined);
  const [currentStep, setCurrentStep] = useState(1);
  const [emissions, setEmissions] = useState(undefined);
  const [prevEmissions, setPrevEmissions] = useState(0);
  const [duration, setDuration] = useState(3);

  useEffect(() => {
    const steps = getBreakdownSteps(heatingMethod, usage, cars);
    setBreakdownSteps(steps);
    setEmissions(steps[currentStep].total);
    setIsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onShowNext = () => {
    if (currentStep < stateArray.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setPrevEmissions(emissions);
      setEmissions(breakdownSteps[nextStep].total);
      setDuration(1);
    }
    if (currentStep === stateArray.length) {
      navigate('/net-zero/interactive');
    }
  };

  const onShowPrev = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setPrevEmissions(emissions);
      setEmissions(breakdownSteps[prevStep].total);
    } else {
      navigate('/net-zero/how-to-with-loop');
    }
  };

  const adjustedCount = currentStep - 1;
  const currentContent = netZeroContent[getStepName(currentStep)];

  const roundedEmissions = emissions ? Math.ceil(emissions) : 0;
  const footerText = currentContent.description
    .replace('{emissions}', roundedEmissions)
    .replace('{year}', new Date().getFullYear() - 1);

  return (
    !isLoading && (
      <CustomCard>
        <BreakdownWrapper>
          <NetZeroHeader
            prevEmissions={prevEmissions}
            emissions={emissions}
            duration={duration}
            adjustedCount={adjustedCount}
            currentContent={currentContent}
          />
          <NetZeroChart
            currentStep={currentStep}
            breakdownSteps={breakdownSteps}
            heatingMethod={heatingMethod}
            cars={cars}
          />
        </BreakdownWrapper>
        <CustomCardFooter className="text-center">
          {footerText}
        </CustomCardFooter>
        <NetZeroFooter
          onShowNext={onShowNext}
          onShowPrev={onShowPrev}
          nextButtonLabel={currentContent.nextButtonLabel}
          currentStepCount={currentStep}
        />
      </CustomCard>
    )
  );
};

BreakdownContainer.propTypes = {
  heatingMethod: PropTypes.string,
  usage: PropTypes.shape({
    electricityUsage: PropTypes.number,
    heatingUsage: PropTypes.number,
  }),
  cars: PropTypes.array,
};

export default BreakdownContainer;
